/**
 * Reproposal's internal-SSO instance — the app's ONE auth setup point.
 *
 * The prebuilt @baltlife/internal-sso-core package can't read this app's build-time
 * env (its own import.meta.env is inlined when the package is built), so this app reads
 * its own env here and hands it over (explicit init). Everything auth-shaped hangs off
 * this instance: the route guard (sso.checkAuthenticated), the api-layer bearer
 * (sso.getToken), the callback exchange (sso.handleOAuthCallback), logout, and
 * proactive session renewal.
 *
 * React components reach it through <InternalSsoProvider sso={sso}> (useSession
 * resolves it from context). import.meta.env.DEV is a real boolean under Vite — the
 * DEV bypass short-circuits the guard so local dev doesn't require a live session.
 */
import { createInternalSso } from '@baltlife/internal-sso-core';

// createInternalSso only fails loud (ConfigError) on a missing/empty option — an
// unreplaced release-pipeline token like "#{Foo}#" is a non-empty string, so it would
// otherwise pass straight through and be used as a literal client_id/URL. Reject that
// shape here before it reaches the package.
const RELEASE_TOKEN = /#\{.+\}#/;
function required(name, value) {
  if (RELEASE_TOKEN.test(value ?? '')) {
    throw new Error(
      `internal-sso config "${name}" is an unreplaced release-pipeline token (${value}).`
    );
  }
  return value ?? '';
}

// DEV guard bypass. On by default under `vite dev` so local work renders without a live
// sign-in. Set VITE_SSO_DEV_BYPASS=false in .env.development to force the REAL
// internal-SSO login on the dev server — e.g. to test against beta with real tokens
// (getToken() only returns a JWT once an actual session exists; the bypass never mints
// one, so bypassed requests send `Bearer null`).
const devBypass =
  import.meta.env.DEV && import.meta.env.VITE_SSO_DEV_BYPASS !== 'false';

export const sso = createInternalSso({
  clientId: required(
    'VITE_INTERNAL_SSO_CLIENT_ID',
    import.meta.env.VITE_INTERNAL_SSO_CLIENT_ID
  ),
  authSiteUrl: required(
    'VITE_AUTH_SITE_URL',
    import.meta.env.VITE_AUTH_SITE_URL
  ),
  tokenEndpoint: required(
    'VITE_INTERNAL_SSO_TOKEN_ENDPOINT',
    import.meta.env.VITE_INTERNAL_SSO_TOKEN_ENDPOINT
  ),
  // The backend the package calls for its own auth endpoints. Must be a bare origin
  // with no path suffix — the package appends its own paths — so any trailing slash
  // (env authoring or release-token replacement) is stripped here.
  apiBaseUrl: required(
    'VITE_EXTHUB_DOMAIN',
    import.meta.env.VITE_EXTHUB_DOMAIN
  ).replace(/\/+$/, ''),
  isDev: devBypass,
  // Per-app storage prefix: namespaces both the session store and (internal-sso-core
  // 0.2.1+) the PKCE txn stash, so tabs served from the same origin don't collide with
  // another app's keys.
  storageKeyPrefix: 'reproposal.auth',
});
